import { useCallback, useEffect, useRef, useState } from "react";
import type { DoczHandle } from "@norkron/web-docz";
import type { AnnotationDraft, Comment } from "../../api/types";
import type { AnchorState } from "../../lib/anchoring";
import type { SurfacePointMap } from "../../lib/reviewOrder";
import {
  type BlockPatch,
  type EditorHostBridge,
  type EditorToolbarConfig,
  type ElaborateEditorHandle,
} from "@norkron/web-elaborate-editor";
import { EditorToolbarMount } from "./EditorToolbarMount";
import {
  CanvasElaborateEditor,
  type SavingEditorHandle,
} from "./CanvasElaborateEditor";
import { ReadCanvas, type ReadCanvasHandle } from "./ReadCanvas";
import { useEditModeFocus } from "./editModeFocus";
import { paperLayoutMode } from "./paperLayoutMode";
import { resolveDocSurface } from "../../lib/canvasEditFlag";
import "./paginatedCanvas.css";
import "./readCanvas.css";

/**
 * `<DoczCanvas>` — the presentational reading/editing surface for a docz
 * resource (brief / article), design §2.1. It is deliberately thin: it mounts a
 * surface and forwards props; it owns no paint and no anchoring.
 *
 *  · READ mode: `<ReadCanvas>` paints the document AND resolves its comment
 *    anchors from the docz blocks. There is no second painter — the hidden
 *    `.ela-doc` DOM twin that used to re-anchor against `rendered_html`, and the
 *    `lib/readCanvasFlag` that chose between them, are both deleted.
 *  · EDIT mode: mounts the paginated `<CanvasElaborateEditor>` over a live
 *    `DoczHandle`. Since the 2026-07-28 pivot this is the ONLY editing surface.
 *
 * `research` never mounts a DoczCanvas — it mounts `<ResearchColumn>` over the
 * markdown editor (the two-editor boundary is enforced at the pane level).
 */

export interface DoczCanvasProps {
  /** Mount the WYSIWYG editor instead of the read canvas. */
  readonly editable?: boolean;
  /** The live docz doc for edit mode. Absent in W1 → read-only; supplied in W2. */
  readonly handle?: DoczHandle | null;
  /** Imperative editor ref — the workbench calls `.toHtml()` on Save (W2). Typed
   *  as a `RefObject` (not `Ref`) so it stays structural across the app's and the
   *  lib's `@types/react` copies (the `RefCallback` union member carries a branded
   *  `VoidOrUndefinedOnly` symbol that differs between the two copies). */
  readonly editorRef?: React.RefObject<SavingEditorHandle | null>;
  /**
   * A gesture the canvas REFUSED, in words the reviewer can read (the host shows
   * it — `ArticleWorkbench` toasts it). Today: a click on a table cell / list item,
   * which is painted text with no addressable caret until the pdfz per-cell
   * provenance + block addressability work lands.
   *
   * Without a sink the refusal is console-only, which is what shipped: a click in
   * a table painted nothing, moved nothing and said nothing, while the caret stayed
   * off-screen and kept taking keystrokes.
   */
  readonly onRefusal?: (message: string) => void;
  /** Fired with each block-scoped, CAS-ready change from the editor. */
  readonly onEditorChange?: (patch: BlockPatch) => void;
  /** className on the surface `<article>` (e.g. `wb-paper` for pure-white paper). */
  readonly surfaceClassName?: string;
  /** className on the EDIT surface's canvas element. (Read mode has no such
   *  element — the reader is `<ReadCanvas>`, which styles itself.) */
  readonly canvasClassName?: string;
  /** `data-testid` on the surface (defaults to `docz-canvas`). */
  readonly testId?: string
  /** Edit-mode only: dock the editor's formatting toolbar into this host node (a
   *  sticky band in the pane header) instead of the inline shell above the paper.
   *  `null` = docked but the header band hasn't mounted yet (toolbar withheld);
   *  `undefined` = inline shell (the lib default). See `@norkron/web-elaborate-editor`. */
  readonly toolbarContainer?: HTMLElement | null;
  /** Edit-mode: the workspace's persisted editor-toolbar config. Drives the
   *  v0.5.0 `<ToolbarShell>` (ribbon | simple) mounted over the editor. When
   *  present, the lib's built-in `FormattingToolbar` is REPLACED by the shell (a
   *  pure projection of the SAME docz-backed `FormatApi`). Omit → no toolbar. */
  readonly toolbarConfig?: EditorToolbarConfig;
  /** Edit-mode: the host actions the toolbar shell needs the workbench to own
   *  (save / export / print / openSettings / addComment / setMode). Structural
   *  inserts (block / page-break) are wired to the editor handle internally. */
  readonly toolbarHost?: Partial<EditorHostBridge>;
  /** Read-mode overlay rendered INSIDE the surface `<article>` (the positioning
   *  context) — the section hover-handle layer lives here so its absolutely-placed
   *  margin grips share `.wb-paper` as their offsetParent (design §2.1). Ignored in
   *  edit mode. */
  readonly overlay?: React.ReactNode;
  /** READ mode: the document id whose docz transport the canvas reader lays out.
   *  Omit it and the pane says it has no document — the correct behaviour for a
   *  surface with no document identity, and no longer a silent fall-through to a
   *  DOM copy. */
  readonly readDocId?: string | number;
  /** READ mode: the comment set the canvas reader anchors. Only ROOTS with a
   *  quote paint a band. Ignored unless the canvas reader mounts. */
  readonly readComments?: readonly Comment[];
  /** READ mode: accessible name + language for the canvas reader's a11y mirror. */
  readonly readTitle?: string;
  readonly readLang?: string;
  /** READ mode: fired when a CANVAS text selection settles into a durable draft.
   *  This is THE composer feed — the DOM `onMouseUp` path it replaced read
   *  `window.getSelection()`, which a canvas selection never populates. */
  readonly onReadCanvasSelect?: (
    draft: AnnotationDraft | null,
    rect: DOMRect | null,
  ) => void;
  /** READ mode: fired when the reviewer CLICKS commented text on the canvas —
   *  the twin of the DOM `.docz-comment` span's click, which opened its thread.
   *  `null` = they clicked text carrying no comment. */
  readonly onReadCanvasSelectComment?: (commentId: number | null) => void;
  /** READ mode: imperative handle on the canvas reader (deep-link scroll-to,
   *  pending band, card→span glow). */
  readonly readCanvasRef?: React.RefObject<ReadCanvasHandle | null>;
  /** READ mode: per-comment anchor states from each re-anchor pass — what feeds
   *  the review rail's exact / moved / orphan badges. */
  readonly onReadCanvasAnchorStates?: (
    states: Record<number, AnchorState>,
  ) => void;
  /** READ mode: where each resolved note sits — the rail's "Document order". */
  readonly onReadCanvasAnchorPoints?: (points: SurfacePointMap) => void;
  /** READ mode: pointer entered/left a comment's painted band (span→card). */
  readonly onReadCanvasHoverComment?: (commentId: number | null) => void;
  /** READ mode: bump to make the canvas reader RE-FETCH the document. The DOM
   *  path repaints whenever its `html` prop changes; the canvas reader fetches
   *  its own transport at mount, so a save under a stable `readDocId` would
   *  otherwise leave it painting the pre-save body. */
  readonly readRevision?: number;
}

export function DoczCanvas(props: DoczCanvasProps) {
  const {
    editable = false,
    handle,
    editorRef,
    onEditorChange,
    surfaceClassName = "ela-canvas",
    canvasClassName = "ela-doc",
    testId = "docz-canvas",
    overlay,
    toolbarContainer,
    toolbarConfig,
    toolbarHost,
    readDocId,
    readComments,
    readTitle,
    readLang,
    onReadCanvasSelect,
    onReadCanvasSelectComment,
    readCanvasRef,
    onReadCanvasAnchorStates,
    onReadCanvasAnchorPoints,
    onReadCanvasHoverComment,
    readRevision,
    onRefusal,
  } = props;

  // ── A REFUSED GESTURE, SPOKEN OUT LOUD ──────────────────────────────────────
  // The canvas is the only surface, so a refusal it does not report is a refusal
  // nobody hears. Deduped by message: a reviewer tapping repeatedly inside a table
  // is told once, not once per press, and the dedupe resets when the message
  // changes so a DIFFERENT refusal is never swallowed by an earlier one.
  // The host supplies the sink (`ArticleWorkbench` passes its toast). No toast
  // CONTEXT is read here on purpose: `<DoczCanvas>` must stay mountable without a
  // provider, which several of its own tests rely on.
  const lastRefusal = useRef<string | null>(null);
  const showRefusal = useCallback(
    (message: string) => {
      if (lastRefusal.current === message) return;
      lastRefusal.current = message;
      onRefusal?.(message);
    },
    [onRefusal],
  );

  // The live editor handle, captured into STATE (not just the imperative
  // `editorRef`) so the `<ToolbarShell>` mount re-renders once the editor is
  // ready and can bind its FormatApi/active snapshot. A merged callback ref feeds
  // BOTH the caller's `editorRef` (Save reads `.toHtml()`) and this state.
  const [editorHandle, setEditorHandle] = useState<ElaborateEditorHandle | null>(
    null,
  );
  const editWrapRef = useRef<HTMLDivElement>(null);
  // Every editor CHANGE bumps this tick and it feeds `<EditorToolbarMount revision>`.
  // The toolbar's enable environment is derived from LIVE probes (undo/redo stack
  // depth, the armed painter) — an edit moves those WITHOUT moving the caret, so
  // the mount's `selectionchange` listener alone would leave the History buttons
  // stale (enabled with an empty stack, or disabled right after the first keystroke).
  const [changeTick, setChangeTick] = useState(0);
  const handleEditorChange = useCallback(
    (patch: BlockPatch) => {
      setChangeTick((n) => n + 1);
      onEditorChange?.(patch);
    },
    [onEditorChange],
  );
  const setMergedRef = useCallback(
    (h: ElaborateEditorHandle | null) => {
      setEditorHandle(h);
      if (editorRef) editorRef.current = h;
    },
    [editorRef],
  );
  // Re-focus the pane's canvas after a toolbar command so typing continues from
  // the caret (the lib's built-in toolbar did this via its own `onCommand`).
  const focusCanvas = useCallback(() => {
    editWrapRef.current
      ?.querySelector<HTMLElement>(".docz-canvas")
      ?.focus();
  }, []);

  // ── THE EDIT BUTTON HANDS THE KEYBOARD OVER ────────────────────────────────
  // Owner-measured live on 2026-07-30: pressing Edit mounted the editor and its
  // input sink correctly and left `document.activeElement` on `<body>`, so EVERY
  // keystroke — not just Backspace — went nowhere until the reviewer clicked the
  // page. This is the ONE place that transition is visible: the `editable` prop
  // is the mode, `editorHandle` is the live surface, and both are here.
  //
  // The policy (edge-triggered, one-shot, never stolen from a text entry the
  // reviewer is typing in) lives in `./editModeFocus` rather than inline, because
  // the naive version of this effect re-fires on every render and yanks focus out
  // of the ribbon and the comment composer. Read that file before changing it.
  useEditModeFocus({ editable, handle: editorHandle, ownRootRef: editWrapRef });

  // ── E2E test-observability seam (read-only) ────────────────────────────────
  // Publish the live editor handle under `window.__ELA_EDITORS__[testId]` so the
  // Playwright toolbar/formatting/table E2E can read the CANONICAL docz IR via
  // `handle.toHtml()` / `handle.activeFormat()` — the same serialization the
  // workbench Save posts — rather than scraping the contentEditable projection.
  // It exposes only the handle the app already holds (logged-in-only surface, no
  // new capability); harmless in production and inert when no editor is mounted.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      __ELA_EDITORS__?: Record<string, ElaborateEditorHandle>;
    };
    const reg = (w.__ELA_EDITORS__ ??= {});
    if (editorHandle) reg[testId] = editorHandle;
    else delete reg[testId];
    return () => {
      delete reg[testId];
    };
  }, [editorHandle, testId]);

  // ── THE DESK RULE — ONE paper, ONE layout mode, BOTH surfaces ──────────────
  // `data-layout-mode` is not decoration: it is the ONLY thing that tells the
  // `.wb-paper` mount whether it is still the manuscript paper (its own
  // `padding: 44px 52px` + hairline border IS the sheet, styles/workbench.css) or
  // merely the DESK a real A4 canvas sits on, in which case that chrome collapses
  // to zero (`.wb-paper[data-layout-mode="paginated"]`, ./paginatedCanvas.css).
  //
  // THE DEFECT THIS CLOSES (owner-reported twice — "why the fuck the read mode
  // page is still smaller than the page in the edit mode?!"). Only the EDIT
  // branch stamped the attribute, as a hardcoded literal. Read mode fell through
  // to the un-stamped rule and kept the paper chrome UNDER a paginated canvas —
  // so the same 793.333px sheet was handed a 697px box in read and an 803px box
  // in edit. Measured on the live app at 1920px, `.ela-read-canvas__pages` had
  // `clientWidth 697` against `scrollWidth 793`: the sheet did not merely look
  // smaller, it was CLIPPED, because scale policy is now natural-size and the
  // page can no longer shrink to fit its box by design.
  //
  // The number was exactly the chrome: 52 + 52 padding + 1 + 1 border = 106px.
  //
  // So the mode is DERIVED from the one fact that decides it — is the visible
  // painter the paginated canvas? — and the same value is stamped by both
  // returns below. This is deliberately NOT a read-mode special case: a special
  // case is what drifted.
  //
  // ⚠️ 2026-07-31: the derivation moved OUT of this file to `./paperLayoutMode`.
  // Deriving it once *here* unified the two surfaces THIS component owns, and
  // that was not enough — `<ArticleWorkbench>`'s past-revision pane mounts its
  // own `.wb-paper` without going through `<DoczCanvas>` at all, so it carried
  // no mode and the owner found the head and its own history rendering in two
  // different boxes. The expression now lives above every mount so a mount that
  // is not this component still cannot drift. Read that file before changing
  // this line.
  // The canvas is THE read surface; the only question left is whether this pane
  // names a document at all. `lib/readCanvasFlag` is gone with the DOM twin it
  // used to select between.
  const showCanvasReader = readDocId != null;
  const layoutMode = paperLayoutMode(
    (editable && handle != null) || showCanvasReader,
  );

  if (editable && handle) {
    // ONE SURFACE (owner directive 2026-07-28 — "focus ONLY on paginated view").
    // Edit mode is the paginated `<CanvasElaborateEditor>` (C0–C3), always. The
    // DOM `contenteditable` editor and the continuous canvas column are gone;
    // `resolveDocSurface` no longer takes an argument because there is nothing
    // left for it to branch on.
    //
    // WHAT THE CANVAS DOES NOT DO. It exposes the same `ElaborateEditorHandle`
    // (so Save reads `.toHtml()` unchanged) and the same `<EditorToolbarMount>`
    // docks over it. With the DOM editor deleted, whatever the handle declines is
    // a PERMANENT product gap, not a "use the other view" gap — so the list is
    // kept honest here and in `./canvasCapabilities`, which is the machine-checked
    // copy (`canvasCapabilities.test.ts` fails CI if a stub is added undeclared).
    //
    // CLOSED 2026-07-29 (the owner's Table-ribbon P0): TABLES and STRUCTURAL
    // INSERTS used to head this list — "all 16 cell verbs + `inTable` (the Table
    // tab never appears)". Both are real now. `inTable` is derived from the
    // caret's own geometry, so the contextual Table tab appears for every table in
    // the document; the 16 verbs run the grid math in `docz.wasm`; and Insert
    // places a real structured table or horizontal rule that paints, takes a
    // caret, and survives save.
    //
    // What is still declined, each for a MEASURED reason recorded beside its
    // group in `./canvasCapabilities`:
    //   · images — `pl_layout` fails the WHOLE document on `srcKind:'path'`, and
    //     `srcKind:'bytes'` cannot carry a payload through the DTO's UTF-8 field
    //   · page breaks — the layout pass ignores a `pageBreak` block and `toHtml()`
    //     renders it as the empty string, so it would not survive a save either
    //   · the content-model insert MENU — an upstream scaffold, no wasm export
    //   · lists — style, nesting, restart, checklist, bullet glyph
    //   · block-kind conversion — PARTIAL: code and multi-line→quote refuse
    //   · agent proposals + block validation
    //   · Find (⌘F) renders disabled
    // Real on the canvas: typing, inline marks, paragraph geometry, page
    // decoration, comment anchors, tables, structural inserts, Save.
    const surface = resolveDocSurface();
    return (
      <div
        className={surfaceClassName}
        data-testid={`${testId}-editor`}
        data-layout-mode={layoutMode}
        data-doc-surface={surface}
        data-canvas-edit="1"
        ref={editWrapRef}
      >
        <CanvasElaborateEditor
          ref={setMergedRef}
          doc={handle}
          onChange={handleEditorChange}
          className={canvasClassName}
          testId={`${testId}-canvas-editor`}
          // A REFUSED GESTURE REACHES THE REVIEWER. Clicking a table cell used to
          // paint nothing, move nothing and say nothing — see
          // `CanvasElaborateEditorProps.onRefusal`. Deduped per message so a reader
          // tapping repeatedly at a table is told once, not once per press.
          onRefusal={showRefusal}
        />
        {/* The toolbar shell — a projection of the docz-backed FormatApi — portals
            into the pane-header dock. `EditorToolbarMount` owns the ribbon⇄simple
            mode (from `toolbarConfig` + the live View▸mode toggle). It must respect
            `editorHandle.capabilities` (see above): the command groups the canvas
            stubs are DENIED there, not just unwired. */}
        {editorHandle && toolbarConfig ? (
          <EditorToolbarMount
            editorHandle={editorHandle}
            doczHandle={handle}
            config={toolbarConfig}
            host={toolbarHost ?? {}}
            container={toolbarContainer ?? null}
            revision={changeTick}
            onAfter={focusCanvas}
          />
        ) : null}
      </div>
    );
  }

  // ── READ MODE — ONE PAINTER ────────────────────────────────────────────────
  // The DOM read copy is GONE (owner directive 2026-07-28, executed 2026-07-30).
  // There is no hidden `.ela-doc` twin re-anchoring comments against
  // `rendered_html` any more: `<ReadCanvas>` resolves anchors from the docz
  // BLOCKS and paints the bands itself, and the review rail is fed from its
  // `onAnchorStates` / `onAnchorPoints` — the same two maps `useDoczCanvas`
  // used to publish, from the surface the reviewer is actually looking at.
  //
  // `onMouseUp` went with it. It read `window.getSelection()`, and a canvas
  // selection is not a DOM selection; select-to-comment now arrives through
  // `<ReadCanvas onSelectText>`. (While both surfaces shared this subtree the DOM
  // handler fired after the canvas one and CANCELLED the draft the reviewer had
  // just made — a collision that could only exist because there were two.)
  //
  // A pane with no `readDocId` paints NOTHING and says so. It used to fall
  // through to the DOM copy, which is precisely the silent second painter this
  // change exists to remove; a surface with no document identity has no document
  // to show, and pretending otherwise is how the twin survived the last pivot.
  return (
    <article
      className={surfaceClassName}
      data-testid={testId}
      data-layout-mode={layoutMode}
      data-read-mode="canvas"
    >
      {showCanvasReader ? (
        <ReadCanvas
          ref={readCanvasRef}
          docId={readDocId}
          comments={readComments ?? []}
          {...(readTitle !== undefined ? { title: readTitle } : {})}
          {...(readLang !== undefined ? { lang: readLang } : {})}
          {...(onReadCanvasSelect ? { onSelectText: onReadCanvasSelect } : {})}
          {...(onReadCanvasSelectComment
            ? { onSelectComment: onReadCanvasSelectComment }
            : {})}
          {...(onReadCanvasAnchorStates
            ? { onAnchorStates: onReadCanvasAnchorStates }
            : {})}
          {...(onReadCanvasAnchorPoints
            ? { onAnchorPoints: onReadCanvasAnchorPoints }
            : {})}
          {...(onReadCanvasHoverComment
            ? { onHoverComment: onReadCanvasHoverComment }
            : {})}
          {...(readRevision !== undefined ? { revision: readRevision } : {})}
          zoom={1}
          testId={`${testId}-read-canvas`}
        />
      ) : (
        <p className="ela-canvas__empty" data-testid={`${testId}-no-document`}>
          This pane has no document to show.
        </p>
      )}
      {/* The section hover-handle layer (design §2.1) — absolutely positioned in
          the paper's left margin, so it is a child of the positioned surface.
          It measures the CANVAS's own page geometry now (see
          `SectionHandleLayer`), not the offsetTop of DOM blocks that no longer
          exist. */}
      {overlay}
    </article>
  );
}
